import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, CircularProgress, DialogContent, DialogTitle, DialogActions, Typography } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { GridList, usePagination, useSettings, Utils } from 'app/main/components';
import { ViewPagination } from 'app/main/components/page-layouts';
import DemsMessagesMailchimpListItem from './DemsMessagesMailchimpListItem';

interface DemsSendToMailchimpDialogProps {
	demsUid: string;
	onSuccess: VoidFunction;
}

export function showSendDemToMailchimpDialog(props: DemsSendToMailchimpDialogProps) {
	return openDialog({
		children: <DemsSendToMailchimpDialog {...props} />,
		fullWidth: true
	});
}

export default function DemsSendToMailchimpDialog({ demsUid, onSuccess }: DemsSendToMailchimpDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('dems');

	const [mailMessages, setMailMessages] = React.useState([]);

	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(value => !value);

	const paginationProps = usePagination({ module: 'dems' });

	const {
		paginatorProps: { nPage, nRows, setMaxRows, isLoading, setIsLoading }
	} = paginationProps;

	React.useEffect(() => {
		setIsLoading(true);
		Utils.executeApi.get({
			url: `/api/mailchimp/messages?pageNumber=${nPage - 1}&rowCount=${nRows}`,
			dispatch,
			successHandler: response => {
				setMailMessages(response.data.templates);
				setMaxRows(response.data.total_items);
				setIsLoading(false);
			}
		});
	}, [dispatch, nPage, nRows, refresh, setIsLoading, setMaxRows]);

	if (!isLoading)
		return (
			<>
				<DialogTitle>{t('DEMS_MESSAGES_DIALOG_TITLE')}</DialogTitle>
				<DialogContent className="flex flex-col p-0" dividers>
					{mailMessages?.length > 0 ? (
						<>
							<GridList
								className="overflow-y-auto"
								settings={settings}
								items={mailMessages}
								itemLayout={layoutProps => (
									<DemsMessagesMailchimpListItem
										{...layoutProps}
										demsUid={demsUid}
										handleNext={onSuccess}
									/>
								)}
								updateRefresh={updateRefresh}
							/>
							<ViewPagination
								module="dems"
								paginationProps={paginationProps}
								updateRefresh={updateRefresh}
								showFilters={false}
								showOrder={false}
								showBorderTop
							/>
						</>
					) : (
						<Typography>
							No templates, go
							<Button
								onClick={() =>
									Utils.openLink({
										pathname: 'http://admin.mailchimp.com/templates/',
										external: true,
										newTab: true
									})
								}
							>
								there
							</Button>
							to create one
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				</DialogActions>
			</>
		);

	return (
		<>
			<DialogTitle>{t('DEMS_MESSAGES_DIALOG_TITLE')}</DialogTitle>
			<DialogContent>
				<CircularProgress disableShrink />
			</DialogContent>
		</>
	);
}
